const GATEWAY_TIMEOUT_STATUS = 504;

export const MAX_RETRIES = 3;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getNumberValue = (value: unknown) =>
  typeof value === 'number' ? value : undefined;

const getStringValue = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

const hasGatewayTimeoutSignature = (value: string) => {
  const normalized = value.toLowerCase();
  return (
    normalized.includes('gateway timeout') ||
    normalized.includes('gateway time-out') ||
    (normalized.includes('504') &&
      (normalized.includes('gateway') || normalized.includes('timeout')))
  );
};

/**
 * PRO-17355: the message this sees is not the browser's raw wording. Sentry's
 * fetch instrumentation rewrites `error.message` in place — see
 * `enhanceFetchErrorMessages` in `@sentry/core/.../instrument/fetch.js` —
 * appending the host of the request it could not reach:
 *
 *   Failed to fetch                        ->  Failed to fetch (api.vrplatform.app)
 *
 * The option defaults to `'always'` and is not set in `Sentry.init`, so the
 * annotated form is what production emits. Both forms have to match: the suffix
 * disappears if that option is ever changed, and Sentry only enhances the three
 * exact browser messages below, so the remaining phrases always arrive bare.
 *
 * The suffix carries `URL.host`, which includes a port when it is not the
 * default (`localhost:8877`). Firefox's wording ends in a period, which the
 * annotation then follows.
 *
 * The anchors still reject API copy that merely reuses the wording, such as
 * `Failed to fetch bank account`.
 */
export type TransportErrorKind =
  | 'failed-to-fetch'
  | 'fetch-failed'
  | 'load-failed'
  | 'network-connection-lost'
  | 'network-error'
  | 'network-request-failed';

const getTransportErrorKindFromMessage = (
  value: string
): TransportErrorKind | undefined => {
  const match =
    /^(typeerror:\s*)?(failed to fetch|load failed|networkerror when attempting to fetch resource|fetch failed|network request failed|network connection lost)\.?(\s*\([^)]*\))?\s*(\n|$)/i.exec(
      value
    );

  switch (match?.[2].toLowerCase()) {
    case 'failed to fetch':
      return 'failed-to-fetch';
    case 'fetch failed':
      return 'fetch-failed';
    case 'load failed':
      return 'load-failed';
    case 'network connection lost':
      return 'network-connection-lost';
    case 'networkerror when attempting to fetch resource':
      return 'network-error';
    case 'network request failed':
      return 'network-request-failed';
    default:
      return undefined;
  }
};

const getStatusFromRecord = (record: Record<string, unknown>) => {
  const status = getNumberValue(record.status);
  if (status !== undefined) return status;

  const statusText = getStringValue(record.status);
  if (statusText) {
    const parsed = Number.parseInt(statusText, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const statusCode = getNumberValue(record.statusCode);
  if (statusCode !== undefined) return statusCode;

  const statusCodeText = getStringValue(record.statusCode);
  if (statusCodeText) {
    const parsed = Number.parseInt(statusCodeText, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return undefined;
};

export const isGatewayTimeoutError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === 'string') {
    return hasGatewayTimeoutSignature(error);
  }

  if (error instanceof Error && hasGatewayTimeoutSignature(error.message)) {
    return true;
  }

  if (!isRecord(error)) return false;

  if (getStatusFromRecord(error) === GATEWAY_TIMEOUT_STATUS) return true;

  const response = error.response;
  if (isRecord(response)) {
    if (getStatusFromRecord(response) === GATEWAY_TIMEOUT_STATUS) return true;
  }

  const context = error.context;
  if (isRecord(context)) {
    if (getStatusFromRecord(context) === GATEWAY_TIMEOUT_STATUS) return true;
  }

  const code = getStringValue(error.code);
  if (code?.toLowerCase() === 'gateway_timeout' || code === '504') return true;

  const message = getStringValue(error.message);
  if (message && hasGatewayTimeoutSignature(message)) return true;

  const nestedError = error.error;
  if (nestedError && nestedError !== error) {
    return isGatewayTimeoutError(nestedError);
  }

  return false;
};

export const getTransportErrorKind = (
  error: unknown
): TransportErrorKind | undefined => {
  if (!error) return undefined;

  if (typeof error === 'string') {
    return getTransportErrorKindFromMessage(error);
  }

  if (error instanceof Error) {
    const kind = getTransportErrorKindFromMessage(error.message);
    if (kind) return kind;
  }

  if (!isRecord(error)) return undefined;

  const message = getStringValue(error.message);
  const messageKind = message
    ? getTransportErrorKindFromMessage(message)
    : undefined;
  if (messageKind) return messageKind;

  const nestedError = error.error;
  if (nestedError && nestedError !== error) {
    const kind = getTransportErrorKind(nestedError);
    if (kind) return kind;
  }

  const cause = error.cause;
  if (cause && cause !== error) {
    const kind = getTransportErrorKind(cause);
    if (kind) return kind;
  }

  const response = error.response;
  if (response && response !== error) {
    const kind = getTransportErrorKind(response);
    if (kind) return kind;
  }

  const context = error.context;
  if (context && context !== error) {
    const kind = getTransportErrorKind(context);
    if (kind) return kind;
  }

  return undefined;
};

export const isFailedToFetchError = (error: unknown): boolean =>
  getTransportErrorKind(error) !== undefined;

export type RetryableReadErrorKind =
  | 'database-query-error'
  | 'connection-error';

export const getRetryableReadErrorKind = (
  error: unknown
): RetryableReadErrorKind | undefined => {
  const message =
    typeof error === 'string'
      ? error
      : isRecord(error)
        ? getStringValue(error.message)
        : undefined;

  switch (message?.toLowerCase()) {
    case 'database query error':
      return 'database-query-error';
    case 'connection error':
    // PRO-18315: Hasura reports an exhausted Postgres connection pool with
    // this message. It is the same transient backend connection failure.
    case 'max connections reached on postgres':
      return 'connection-error';
    default:
      return undefined;
  }
};

export function retry(failureCount: number, error: unknown): boolean {
  // PRO-17536/PRO-17538: retry exact transient database errors once without
  // multiplying sustained load.
  if (getRetryableReadErrorKind(error) === 'database-query-error') {
    return failureCount < 1;
  }

  if (isGatewayTimeoutError(error) || isFailedToFetchError(error)) {
    return failureCount < MAX_RETRIES;
  }

  return false;
}

export function retryHasuraQuery(
  failureCount: number,
  error: unknown
): boolean {
  // PRO-17932: Hasura surfaced a transient backend connection failure inside
  // an HTTP-200 GraphQL response. Keep this retry specific to read queries so
  // REST and GraphQL mutation policies remain unchanged.
  if (getRetryableReadErrorKind(error) === 'connection-error') {
    return failureCount < 1;
  }

  return retry(failureCount, error);
}

/**
 * Methods that are idempotent by definition, so replaying one cannot add a
 * second side effect.
 */
const IDEMPOTENT_HTTP_METHODS = new Set([
  'get',
  'head',
  'options',
  'put',
  'delete',
]);

/**
 * PRO-17355: widening the transport signature would otherwise start replaying
 * writes that used to fail on the first attempt. A transport failure carries no
 * response at all, so it says nothing about whether the API applied the write
 * before the connection dropped, and portal writes have no idempotency key —
 * `POST /transactions/{id}/pay` is asserted not to send one. PRO-15652 wrote
 * down the rule this follows: treat a network failure on a write as
 * indeterminate unless the API has an idempotency contract.
 *
 * So a transport failure on `post` or `patch` surfaces to the user instead of
 * being replayed, the way CSV import already does with its own unknown-status
 * warning. Reads and idempotent methods keep the shared policy.
 *
 * Gateway timeouts are deliberately left alone for every method. That predates
 * this issue and `api-mutation-placement.spec.tsx` covers it — an in-flight
 * `POST /teams` retry has to stay bound to its starting placement — so changing
 * it is a separate decision, not a side effect of this fix. A 504 leaves a write
 * just as indeterminate, which makes it worth revisiting on its own.
 *
 * A caller whose endpoint does have an idempotency contract can pass its own
 * `retry` to opt back in.
 */
export const retryIdempotentMethod =
  (method: string) =>
  (failureCount: number, error: unknown): boolean => {
    if (isGatewayTimeoutError(error)) return failureCount < MAX_RETRIES;

    if (!IDEMPOTENT_HTTP_METHODS.has(method.toLowerCase())) return false;

    return retry(failureCount, error);
  };
